import React, { useState } from 'react';
import { X, CreditCard, Smartphone, CheckCircle } from 'lucide-react';
import { CartItem, Order, Address } from '../../types';
import { showToast } from '../../utils/toast';
import toast from 'react-hot-toast';
import { createStripeCheckoutSession } from '../../services/stripeService';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  cart: CartItem[];
  cartTotal: number;
  onCheckout: (order: Omit<Order, 'id'>) => void;
  currentUser: any;
  onOrderConfirmation: (orderId: string) => void;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  cart,
  cartTotal,
  onCheckout,
  currentUser,
  onOrderConfirmation
}) => {
  const [paymentMethod, setPaymentMethod] = useState<'momo' | 'card'>('momo');
  const [shippingInfo, setShippingInfo] = useState<Address>({
    street: '',
    city: '',
    region: '',
    country: 'Ghana',
    phone: '',
    postalCode: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onClose();
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!shippingInfo.street.trim()) errors.street = 'Street address is required';
    if (!shippingInfo.city.trim()) errors.city = 'City is required';
    if (!shippingInfo.region.trim()) errors.region = 'Region is required';
    if (!shippingInfo.phone.trim()) errors.phone = 'Phone number is required';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCheckout = async () => {
    if (!validateForm()) {
      showToast.form.validationError('shipping information');
      return;
    }
    
    setIsProcessing(true);
    
    // Show processing toast
    const processingToast = toast.loading('Processing your order...');
    
    try {
      // Create a unique order ID
      const orderId = `ORD-${Date.now()}`;
      
      // Create order object
      const order: Omit<Order, 'id'> = {
        userId: currentUser.id,
        items: cart,
        total: cartTotal,
        status: 'pending',
        paymentMethod,
        paymentStatus: 'pending',
        shippingAddress: shippingInfo,
        orderDate: new Date().toISOString()
      };
      
      if (paymentMethod === 'card') {
        try {
          // Create Stripe checkout session
          const checkoutSession = await createStripeCheckoutSession({
            items: cart.map(item => ({
              name: item.product.name,
              description: item.product.description,
              amount: Math.round(item.product.price * 100), // Convert to cents
              quantity: item.quantity
            })),
            customerEmail: currentUser.email,
            metadata: {
              orderId,
              userId: currentUser.id
            },
            successUrl: `${window.location.origin}/profile?order=${orderId}&status=success`,
            cancelUrl: `${window.location.origin}/profile?order=${orderId}&status=cancelled`
          });
          
          // Save order to database
          onCheckout(order);
          
          // Dismiss processing toast
          toast.dismiss(processingToast);
          
          // Redirect to Stripe checkout
          window.location.href = checkoutSession.url;
        } catch (error) {
          console.error('Card payment error:', error);
          
          // Provide user-friendly error messages
          if (error instanceof Error) {
            toast.error(`Card payment failed: ${error.message}`, { id: processingToast });
          } else {
            toast.error('Card payment failed. Please try Mobile Money or contact support.', { id: processingToast });
          }
          
          // Don't throw here, let user try mobile money instead
          setIsProcessing(false);
          return;
        }
      } else {
        // For Mobile Money, we'll integrate with a real mobile money provider
        // For now, we'll simulate a successful payment
        
        // Simulate payment processing
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Update order status
        order.status = 'confirmed';
        order.paymentStatus = 'paid';
        
        // Save order to database
        onCheckout(order);
        
        // Close modal and show confirmation
        onClose();
        onOrderConfirmation(orderId);
        
        // Update toast
        toast.success('Payment successful! Your order has been placed.', { id: processingToast });
      }
    } catch (error) {
      console.error('Checkout error:', error);
      
      // Provide specific error messages
      if (error instanceof Error) {
        toast.error(`Checkout failed: ${error.message}`, { id: processingToast });
      } else {
        toast.error('Checkout failed. Please try again or contact support.', { id: processingToast });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="card-base w-full max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-custom dark:bg-gray-900 dark:shadow-xl dark:border dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Checkout</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            disabled={isProcessing}
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Shipping Information */}
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Shipping Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Street Address *
                </label>
                <input
                  type="text"
                  placeholder="Street Address"
                  value={shippingInfo.street}
                  onChange={(e) => setShippingInfo({...shippingInfo, street: e.target.value})}
                  className={`input-base ${validationErrors.street ? 'input-error' : ''}`}
                />
                {validationErrors.street && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.street}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  City *
                </label>
                <input
                  type="text"
                  placeholder="City"
                  value={shippingInfo.city}
                  onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                  className={`input-base ${validationErrors.city ? 'input-error' : ''}`}
                />
                {validationErrors.city && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.city}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Region *
                </label>
                <input
                  type="text"
                  placeholder="Region"
                  value={shippingInfo.region}
                  onChange={(e) => setShippingInfo({...shippingInfo, region: e.target.value})}
                  className={`input-base ${validationErrors.region ? 'input-error' : ''}`}
                />
                {validationErrors.region && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.region}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={shippingInfo.phone}
                  onChange={(e) => setShippingInfo({...shippingInfo, phone: e.target.value})}
                  className={`input-base ${validationErrors.phone ? 'input-error' : ''}`}
                />
                {validationErrors.phone && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.phone}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Payment Method - MoMo First */}
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Select Payment Method</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setPaymentMethod('momo')}
                className={`p-4 border-2 rounded-xl transition-all ${
                  paymentMethod === 'momo' 
                    ? 'border-green-500 bg-green-50 dark:bg-green-950 dark:border-green-600' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Smartphone className="text-green-600 dark:text-green-400" size={24} />
                  <div className="text-left">
                    <p className="font-medium dark:text-gray-100">Mobile Money</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">MTN • Vodafone • AirtelTigo</p>
                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                      Recommended
                    </p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => setPaymentMethod('card')}
                className={`p-4 border-2 rounded-xl transition-all ${
                  paymentMethod === 'card' 
                    ? 'border-green-500 bg-green-50 dark:bg-green-950 dark:border-green-600' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <CreditCard className="text-blue-600 dark:text-blue-400" size={24} />
                  <div className="text-left">
                    <p className="font-medium dark:text-gray-100">Credit/Debit Card</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Visa • Mastercard</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
          
          {/* Order Summary */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Order Summary</h4>
            <div className="space-y-3">
              {cart.map((item) => (
                <div key={item.productId} className="flex justify-between text-sm">
                  <span className="flex-1 dark:text-gray-300">{item.product.name} × {item.quantity}</span>
                  <span className="font-medium dark:text-gray-100">${(item.product.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 flex justify-between font-semibold text-lg">
                <span className="dark:text-gray-100">Total</span>
                <span className="dark:text-gray-100">${cartTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          {/* Payment Information */}
          {paymentMethod === 'momo' && (
            <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="text-green-600 dark:text-green-400 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="font-medium text-green-800 dark:text-green-300">Mobile Money Payment</p>
                  <p className="text-green-700 dark:text-green-400 text-sm mt-1">
                    After clicking 'Complete Purchase', you'll receive a prompt on your phone to authorize the payment.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {paymentMethod === 'card' && (
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <CreditCard className="text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="font-medium text-blue-800 dark:text-blue-300">Secure Card Payment</p>
                  <p className="text-blue-700 dark:text-blue-400 text-sm mt-1">
                    You'll be redirected to our secure payment processor to complete your purchase.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Confirm Purchase */}
          <button
            onClick={handleCheckout}
            disabled={isProcessing}
            className="btn-primary w-full py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Processing Payment...</span>
              </div>
            ) : (
              `Complete Purchase - ${paymentMethod === 'momo' ? 'Mobile Money' : 'Card'}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;